import React from 'react';
import { X, ArrowRight, RotateCcw } from 'lucide-react';
import Button from '../ui/Button';
import { ruleKey, RULE_OP_SYMBOL } from '../../services/offerStudioService';

/**
 * Explore rules: the set of (feature = value) conditions the user has picked
 * from findings. Phase A only accumulates them for the "Build segment" deep
 * link; live re-computation of the filtered population is Phase C.
 */
export default function RulesPanel({ rules, match, onMatchChange, onRemove, onReset, onBuildSegment, presets, onAddPreset, isRuleActive }) {
  return (
    <section className="rounded-lg border bg-white" aria-label="Rules">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="text-xs uppercase tracking-wide text-gray-500">Rules</span>
        <div className="inline-flex rounded border text-xs overflow-hidden" role="radiogroup" aria-label="Match">
          {['ALL', 'ANY'].map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={match === m}
              onClick={() => onMatchChange(m)}
              className={`px-2 py-1 ${match === m ? 'bg-[#0062FB] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      <div className="p-3 space-y-2">
        {rules.length === 0 ? (
          <p className="text-xs text-gray-500">
            Add findings as rules from the insights or any feature row, then build a segment from them. Over-indexed
            findings become inclusions (=), under-indexed ones exclusions (≠).
          </p>
        ) : (
          <ul className="space-y-1">
            {rules.map((r) => (
              <li key={ruleKey(r)} className="flex items-center justify-between gap-2 rounded bg-gray-50 px-2 py-1 text-sm">
                <span className="truncate">
                  <span className="text-gray-500">{r.display_name}</span>{' '}
                  <span className={r.offer_direction === 'under' ? 'text-red-700' : 'text-gray-500'}>{RULE_OP_SYMBOL[r.operator] || r.operator}</span>{' '}
                  <span className="font-medium">
                    {r.operator === 'is_null' || r.operator === 'is_not_null' ? '' : r.offer_value_label || r.value}
                    {r.operator === 'between' && r.value2 ? ` – ${r.value2}` : ''}
                  </span>
                  {!r.column ? (
                    <span className="ml-1 text-[10px] uppercase text-amber-600" title="No exact profile column for this attribute; you'll pick one in the segment editor">
                      pick column
                    </span>
                  ) : (
                    <span className="ml-1 text-[10px] text-gray-400" title={`${r.schema}.${r.table}.${r.column} ${r.operator} ${r.value}${r.value2 ? ` and ${r.value2}` : ''}`}>
                      → {r.column}
                    </span>
                  )}
                </span>
                <button type="button" onClick={() => onRemove(r)} className="p-0.5 text-gray-400 hover:text-red-600" aria-label={`Remove rule ${r.display_name}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-2 pt-1">
          <Button variant="outline" size="sm" className="flex-1" disabled={rules.length === 0} onClick={onBuildSegment}>
            Build segment <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
          <button type="button" onClick={onReset} disabled={rules.length === 0} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-40" aria-label="Reset rules">
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
        {presets?.length ? (
          <div className="pt-2 border-t">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Start with</div>
            <div className="flex flex-wrap gap-1">
              {presets.map((p) => {
                const active = isRuleActive?.(p);
                return (
                  <button
                    key={`${p.feature}::${p.value}`}
                    type="button"
                    onClick={() => onAddPreset(p)}
                    disabled={active}
                    className={`rounded-full border px-2 py-0.5 text-xs ${active ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 text-gray-700 hover:border-[#0062FB] hover:text-[#0062FB]'}`}
                    title={p.text}
                  >
                    <span className="text-gray-500">{p.display_label}:</span> {p.value_label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
